// La liste des personnes a mentionner, sous la zone de saisie du fil
// (feat-feedback-les-mentions-de-personnes ; prototype MentionTextarea.tsx:114-127, F-FBK-25 et
// F-FBK-28). Liste de choix ARIA (listbox) : la zone de saisie garde le focus et pilote la
// surbrillance au clavier (fleches, Entree, Tab, Echap) ; un clic choisit la personne.
import { useEffect, useState } from 'react';
import { useTranslation } from '@gs/i18n';
import type { MentionableDto } from '../schemas';
import { MENTION_LIST_ID, mentionOptionId, useMentionables } from './mentionsQuery';

const AUCUNE_PERSONNE: MentionableDto[] = [];

interface MentionListProps {
  personnes: MentionableDto[];
  surbrillance: number;
  chargement: boolean;
  onChoisir: (personne: MentionableDto) => void;
}

export function MentionList({ personnes, surbrillance, chargement, onChoisir }: MentionListProps) {
  const { t } = useTranslation();
  return (
    <ul id={MENTION_LIST_ID} role="listbox" aria-label={t('feedback:thread.mentionListAria')}
      className="absolute bottom-full left-0 z-20 mb-1 max-h-56 w-72 overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-xs dark:border-slate-700 dark:bg-slate-900">
      {chargement && personnes.length === 0 && (
        <li className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">{t('feedback:thread.mentionLoading')}</li>
      )}
      {!chargement && personnes.length === 0 && (
        <li className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">{t('feedback:thread.mentionNone')}</li>
      )}
      {personnes.map((p, i) => (
        <li key={p.id} id={mentionOptionId(i)} role="option" aria-selected={i === surbrillance}
          // mousedown et non click : la zone de saisie ne perd pas le focus avant le choix.
          onMouseDown={(e) => { e.preventDefault(); onChoisir(p); }}
          className={`touch-manipulation cursor-pointer px-3 py-2 text-sm ${i === surbrillance
            ? 'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-100'
            : 'text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800'}`}>
          {p.name}
        </li>
      ))}
    </ul>
  );
}

interface MentionPickerProps {
  ticketId: string;
  /** Ce qui suit le « @ » au curseur. */
  saisie: string;
  surbrillance: number;
  onChoisir: (personne: MentionableDto) => void;
  /** Rend la liste affichee au compositeur, qui pilote le clavier (F-FBK-28). */
  onPersonnes: (personnes: MentionableDto[]) => void;
}

/** La recherche (200 ms apres la derniere frappe : plafond serveur par usager) et sa liste. */
export function MentionPicker({ ticketId, saisie, surbrillance, onChoisir, onPersonnes }: MentionPickerProps) {
  const [differee, setDifferee] = useState<string | null>(null);
  useEffect(() => {
    const id = window.setTimeout(() => setDifferee(saisie), 200);
    return () => window.clearTimeout(id);
  }, [saisie]);
  const q = useMentionables(ticketId, differee);
  // Reference stable quand rien n est encore charge : un tableau neuf a chaque rendu relancerait
  // l effet ci-dessous sans fin (le compositeur rerend a chaque onPersonnes).
  const personnes = q.data ?? AUCUNE_PERSONNE;
  useEffect(() => { onPersonnes(personnes); }, [personnes, onPersonnes]);
  return (
    <MentionList personnes={personnes} surbrillance={surbrillance}
      chargement={q.isFetching || differee !== saisie} onChoisir={onChoisir} />
  );
}
